import { assign, setup } from 'xstate';
import { sentryReporting } from '../../common/sentry';

const validateInput = (name, value) => {
	return !value ? `${name === 'username' ? 'Email Address' : 'Password'} is required` : null;
};

export const loginMachine = setup({
	guards: {
		hasErrors: ({ context }) => Object.keys(context.errors).length > 0,
		isLoginMissing: ({ context }) => {
			const { username, password } = context.loginValues || {};
			return !username || !password;
		},
		hasLoginFailed: ({ event }) => !event?.output?.success,
	},
	actions: {
		updateLoginValues: assign({
			loginValues: ({ context, event }) => ({
				...context.loginValues,
				[event.params.name]: event.params.value,
			}),
			errors: ({ context }) => {
				let updatedErrors = { ...context.errors };
				delete updatedErrors.loginAlert;
				delete updatedErrors.loginError;
				return updatedErrors;
			},
		}),

		validateInput: assign({
			errors: ({ context, event }) => {
				const { name, value } = event.params;
				let updatedErrors = { ...context.errors };
				const validationMessage = validateInput(name, value);

				if (validationMessage) updatedErrors[name] = validationMessage;
				else delete updatedErrors[name];
				return updatedErrors;
			},
		}),

		validateLoginValues: assign({
			errors: ({ context }) => {
				const { username, password } = context.loginValues;
				const usernameError = validateInput('username', username);
				const passwordError = validateInput('password', password);

				const updatedErrors = {
					...context.errors,
					...(usernameError && { username: usernameError }),
					...(passwordError && { password: passwordError }),
				};

				return updatedErrors;
			},
		}),

		handleLoginErrors: assign({
			errors: ({ context, event, self }) => {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'loginFail',
				});

				const { output = {} } = event;
				const { data = {}, alert, error } = output;
				const errorMessage = error?.message || data.error?.message;
				const alertMessage = alert?.message || data.alert?.message;
				const ignoredMessages = {
					alerts: [],
					errors: ['There was a problem logging in. Please check your username or password and try again.'],
				};
				const isValidError = errorMessage && !ignoredMessages.errors.includes(errorMessage);
				const isValidAlert = alertMessage && !ignoredMessages.alerts.includes(alertMessage);

				if (isValidError) {
					sentryReporting(event, self);
				}

				const updatedErrors = {
					...context.errors,
					...(isValidAlert && { loginAlert: alertMessage }),
					...(isValidAlert && { loginError: errorMessage }),
				};

				return updatedErrors;
			},
		}),

		dataLayerUpdate: () => {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'login',
			});
		},
	},
}).createMachine({
	id: 'userLogin',
	initial: 'edit',
	context: ({ input }) => {
		return {
			loginType: input.loginType,
			requestedUrl: input.requestedUrl,
			loginValues: {
				username: '',
				password: '',
			},
			errors: {},
		};
	},
	states: {
		edit: {
			on: {
				INPUT_CHANGE: {
					actions: ['updateLoginValues', 'validateInput'],
				},
				SUBMIT_LOGIN: [
					{
						guard: 'hasErrors',
						target: 'edit',
					},
					{
						guard: 'isLoginMissing',
						target: 'edit',
						actions: 'validateLoginValues',
					},
					{
						target: 'loggingIn',
					},
				],
			},
		},
		loggingIn: {
			invoke: {
				id: 'login',
				src: 'login',
				input: ({ context }) => ({
					user: {
						username: context.loginValues.username,
						password: context.loginValues.password,
					},
				}),
				onDone: [
					{
						guard: 'hasLoginFailed',
						target: 'edit',
						actions: ['handleLoginErrors', 'resetOkta'],
					},
					{
						actions: ['dataLayerUpdate', 'postLoginAction'],
					},
				],
			},
		},
	},
});
